/* adg-index.js — the one place the artwork index is loaded.
 *
 * Before this, three scripts each ran their own fetch() of the same URL:
 * adg-artgrid.js, adg-cat-tiles.js and byo-builder.js. On a collection page
 * carrying both tiles and a grid that is two concurrent requests for the
 * same bytes — the browser cache does not de-duplicate requests already in
 * flight. One promise, shared, fixes it.
 *
 * It also absorbs the v3 file format so nothing downstream has to care.
 * v3 interns the repeated strings — artist names, collection handles and
 * the CDN path segment live once in meta tables and each row holds an
 * index — which is most of the reason the file shrank. Consumers get rows
 * back in the flat shape they already read (a, o, c, img, img_full), so
 * adopting this is a one-line change at each call site.
 *
 * FORWARD COMPATIBILITY. The file is written by the backend and the theme
 * ships separately, so the two are never in lockstep: a new file has to
 * work against an old theme, and an old file against a new theme. Hence
 * meta.v is compared with >=, unknown keys are passed straight through
 * rather than dropped, and every added field is optional.
 */
(function (global) {
  'use strict';

  // Run once per page, however many times we are included. A page can carry
  // two adg-categories sections (templates/list-collections.json does), each
  // emitting its own <script> tag; without this the file re-executes, the
  // memoised promise below is reset to null, and the "one fetch" guarantee
  // this module exists for quietly becomes two.
  if (global.ADGIndex) return;

  var _promise = null;

  function slug(s) {
    return String(s || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  }

  /* The URL rule travels WITH the data (meta.img), so the CDN layout can
   * change in a backend deploy without a theme release. */
  function urlFor(meta, row, width, fmt) {
    if (row.u) return row.u;                    // explicit override
    var img = (meta && meta.img) || {};
    if (row.s == null || !meta.segs || !img.base) return '';
    return img.base + '/' + meta.segs[row.s] + '/' + row.h + '/' + width + '.' + fmt;
  }

  function widths(meta) {
    return ((meta && meta.img && meta.img.widths) || [256, 512, 800, 1024]);
  }

  /* One row, expanded into the shape the existing grids and builders read.
   * Unknown keys ride along untouched — that is what lets the backend add a
   * field and have it reach a consumer that already knows what to do with
   * it, without a coordinated release. */
  function expand(meta, row) {
    var out = {};
    for (var k in row) if (Object.prototype.hasOwnProperty.call(row, k)) out[k] = row[k];

    var names = (row.a || []).map(function (i) { return meta.artists[i]; })
                             .filter(Boolean);
    out.artists = names;                       // every credited artist
    out.a  = names[0] || '';                   // primary, for display
    out.ah = slug(out.a);
    out.o  = row.o === 1 ? 'landscape' : 'portrait';
    /* Available formats (index builder's availability pass, 2026-08-23):
       which per-design products are PUBLISHED right now. Absent on rows
       from an older index — consumers must treat missing as unknown. */
    if (row.f && meta.fmts) out.f = row.f.map(function (i) { return meta.fmts[i]; });
    out.c  = (row.c || []).map(function (i) { return meta.collections[i]; })
                          .filter(Boolean);

    var w = widths(meta);
    var card = w.indexOf(512) > -1 ? 512 : w[Math.min(1, w.length - 1)];
    out.img      = urlFor(meta, row, card, 'jpg');
    out.img_full = urlFor(meta, row, w[w.length - 1] >= 1024 ? 1024 : card, 'jpg');

    /* Everything needed to build any rung, as ONE short string. Deliberately
     * not pre-building srcset strings here: at 3,103 rows that would be
     * megabytes of text held for cards that mostly never render. The card
     * builder expands this for the two dozen actually on screen. */
    if (row.s != null && meta.segs && meta.img && meta.img.base) {
      out.cdn = meta.img.base + '/' + meta.segs[row.s] + '/' + row.h;
    }
    return out;
  }

  function load(url) {
    if (_promise) return _promise;
    _promise = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      })
      .then(function (doc) {
        var meta = doc.meta || {};
        if ((meta.v || 0) < 3) throw new Error('index too old: v' + meta.v);
        var rows = (doc.artworks || []).map(function (row) {
          return expand(meta, row);
        });
        return { meta: meta, rows: rows };
      })
      .catch(function (err) {
        _promise = null;                        // a failure must not be sticky
        throw err;
      });
    return _promise;
  }

  global.ADGIndex = {
    load: load,

    /* Pick a rung off the ladder. WebP where the browser takes it — measured
     * on this catalogue it is 39% smaller at 512 and 46% at 1024. */
    url: function (meta, row, width, fmt) {
      return urlFor(meta, row, width, fmt || 'webp');
    },

    srcset: function (meta, row, fmt) {
      return widths(meta).filter(function (w) { return w <= 1024; })
        .map(function (w) {
          return urlFor(meta, row, w, fmt || 'webp') + ' ' + w + 'w';
        }).join(', ');
    },

    /* The 32px render the pipeline already makes — 593 bytes. Worth one
     * extra request on a lightbox or a product hero, not on a grid card. */
    lqip: function (meta, row) {
      var n = (meta.img && meta.img.lqip) || 32;
      return urlFor(meta, row, n, 'jpg');
    },

    /* Rungs a grid card should offer. Capped at 1024 — a card is never
     * displayed larger than that, and 2048 is 338 KB of nobody's business. */
    CARD_WIDTHS: [256, 512, 800, 1024],

    /* Built per rendered card, from row.cdn, not held for all 3,103. */
    setFrom: function (prefix, fmt) {
      return this.CARD_WIDTHS.map(function (w) {
        return prefix + '/' + w + '.' + fmt + ' ' + w + 'w';
      }).join(', ');
    },

    _reset: function () { _promise = null; }    // tests
  };
})(window);
